import { ILiveTranscriptSegment } from 'services/callScribe/types';
import { normaliseSpeaker, participantKey } from 'services/callScribe/participants';

/**
 * Identity for a committed segment. `seq` is NOT a key: the coordinator restarts
 * it per speaker lane and a single segment window can close twice, so one call
 * legitimately contains several rows with `seq = 1` (observed on call 123).
 * Keying on it silently dropped whichever row arrived second.
 *
 * The key must also be derivable from BOTH sources that feed the panel — the
 * REST catch-up and the SignalR stream — or the same segment would render twice.
 * REST rows carry a database `id`; the wire event does not. The shared Twilio
 * media clock is present on both, so the span identifies the segment, with
 * `seq` appended only to separate rows that share a span.
 */
export const transcriptSegmentKey = (segment: ILiveTranscriptSegment): string => {
  const start = segment.startMs ?? 'n';
  const end = segment.endMs ?? 'n';
  return `${participantKey(segment)}|${start}|${end}|${segment.seq}`;
};

export const liveTranscriptSegmentKey = transcriptSegmentKey;

/** Speech order: media-clock onset first, then speaker, then span end. */
export const compareLiveTranscriptSegments = (
  a: ILiveTranscriptSegment,
  b: ILiveTranscriptSegment,
): number => {
  const aMs = a.startMs ?? Number.MAX_SAFE_INTEGER;
  const bMs = b.startMs ?? Number.MAX_SAFE_INTEGER;
  const aEnd = a.endMs ?? Number.MAX_SAFE_INTEGER;
  const bEnd = b.endMs ?? Number.MAX_SAFE_INTEGER;
  const aKey = participantKey(a);
  const bKey = participantKey(b);
  return (
    aMs - bMs ||
    normaliseSpeaker(a.speaker) - normaliseSpeaker(b.speaker) ||
    (aKey < bKey ? -1 : aKey > bKey ? 1 : 0) ||
    aEnd - bEnd ||
    a.seq - b.seq
  );
};
